import type { FC } from 'react';
import React, { Fragment, useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { ListItemText, ListItemIcon, Collapse, ListItemButton } from '@mui/material';
import { ExpandLess, ExpandMore, BarChart, ShowChart, Analytics } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import AppRoutes from '@/app/utils/routes.ts';
import * as GuiStateSelectors from 'app/selectors/guiStateSelectors.ts';
import { GuiStateActions } from '@/app/actions/guiState/guiStateAction.ts';
import { StyledListItem, StyledListItemIcon } from '../styles.ts';
import { styled } from '@mui/material/styles';

const StyledStatisticsListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '6rem',
  minHeight: '3rem',
  display: 'flex',
  gap: theme.spacing(2),
  padding: theme.spacing(0.8, 2, 0.8, 2),
  '&.Mui-selected': {
    backgroundColor: theme.palette.secondary[100]
  },
  '&.Mui-selected:hover': {
    backgroundColor: theme.palette.primary[100]
  },
  '&:hover': {
    backgroundColor: theme.palette.primary[100]
  }
}));

const StatisticsSection: FC = () => {
  const [translate] = useTranslation(I18nNamespace.Common);
  const dispatch = useDispatch();
  const sideBarOpenState = useSelector(GuiStateSelectors.sideBarStateSelector);
  const [open, setOpen] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const statisticsRouteMatch = useRouteMatch({
    path: AppRoutes.Statistics,
    strict: false
  });

  const usageStatisticsRouteMatch = useRouteMatch({
    path: AppRoutes.UsageStatistics,
    strict: false
  });

  const isAnyStatisticsRouteActive = statisticsRouteMatch || usageStatisticsRouteMatch;

  // Auto-expand section when on a statistics route
  useEffect(() => {
    if (isAnyStatisticsRouteActive) {
      setOpen(true);
    }
  }, [isAnyStatisticsRouteActive]);

  // Scroll into view when section expands
  useEffect(() => {
    if (open && sectionRef.current) {
      // Small delay to allow Collapse animation to start
      setTimeout(() => {
        sectionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }, 100);
    }
  }, [open]);

  const handleClick = () => {
    if (!sideBarOpenState) {
      dispatch(GuiStateActions.sideMenuOpen(true));
      setOpen(true);
      return;
    }
    setOpen(!open);
  };

  return (
    <>
      <StyledStatisticsListItemButton ref={sectionRef} selected={!!isAnyStatisticsRouteActive} onClick={handleClick}>
        <StyledListItemIcon>
          <BarChart />
        </StyledListItemIcon>
        {sideBarOpenState && (
          <Fragment>
            <ListItemText primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.statistics))} />
            {open ? <ExpandLess /> : <ExpandMore />}
          </Fragment>
        )}
      </StyledStatisticsListItemButton>
      {sideBarOpenState && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <StyledListItem
            selected={!!statisticsRouteMatch}
            data-cy="historical-statistics"
            component={Link}
            to={AppRoutes.Statistics}
            sx={{ paddingLeft: 4 }}
          >
            <StyledListItemIcon>
              <ShowChart />
            </StyledListItemIcon>
            <ListItemText
              primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.statisticsSubItems.historical))}
            />
          </StyledListItem>
          <StyledListItem
            selected={!!usageStatisticsRouteMatch}
            data-cy="usage-statistics"
            component={Link}
            to={AppRoutes.UsageStatistics}
            sx={{ paddingLeft: 4 }}
          >
            <StyledListItemIcon>
              <Analytics />
            </StyledListItemIcon>
            <ListItemText
              primary={translate(nameof.full<I18nCommonNs>((l) => l.navigation.statisticsSubItems.usage))}
            />
          </StyledListItem>
        </Collapse>
      )}
    </>
  );
};

export default StatisticsSection;
